import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Download, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { openTransportPdf } from "@/lib/pdf";

export default function DriverDashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/transports").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <div className="p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="label-caps text-muted-foreground mb-2">Salut, {user?.name?.split(" ")[0] || "chauffeur"}</div>
            <h1 className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">Mes transports</h1>
            <p className="text-muted-foreground mt-2">Consultez et remplissez vos bons de transport.</p>
          </div>
          <Link to="/chauffeur/nouveau">
            <Button data-testid="new-transport-btn" className="h-12 px-6 rounded-none bg-primary text-primary-foreground font-bold btn-hard active:scale-95">
              <Plus className="w-4 h-4 mr-2" /> Nouveau transport
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="label-caps text-muted-foreground">Chargement…</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center border-2 rounded-none border-dashed">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-display font-bold text-xl mb-1">Aucun transport pour l'instant</h3>
            <p className="text-muted-foreground text-sm mb-4">Créez votre premier bon de transport.</p>
            <Link to="/chauffeur/nouveau">
              <Button className="rounded-none bg-primary text-primary-foreground btn-hard">
                <Plus className="w-4 h-4 mr-2" /> Commencer
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="grid gap-4">
            {items.map((t) => (
              <Card key={t.id} data-testid={`transport-card-${t.id}`} className="border-2 rounded-none p-5 md:p-6 hover:border-primary transition-colors">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 tick-line pl-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-mono-alt text-xs bg-black text-white px-2 py-1">
                        {t.po_number ? `P.O. ${t.po_number}` : `#${t.id.slice(-6).toUpperCase()}`}
                      </span>
                      <Badge className="rounded-none bg-secondary text-secondary-foreground border border-border">{t.transport_date}</Badge>
                      {t.vehicle_type && <Badge className="rounded-none bg-primary text-primary-foreground">{t.vehicle_type}</Badge>}
                    </div>
                    <h3 className="font-display font-black text-xl truncate">{t.client_name}</h3>
                    <div className="text-sm text-muted-foreground mt-1 truncate">
                      {t.vehicle_make_model} {t.vehicle_year && `· ${t.vehicle_year}`} {t.vehicle_plate && `· ${t.vehicle_plate}`}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      className="h-11 rounded-none border-2"
                      onClick={() => openTransportPdf(t.id)}
                      data-testid={`pdf-${t.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" /> PDF
                    </Button>
                    <Link to={`/chauffeur/transport/${t.id}`}>
                      <Button data-testid={`open-transport-${t.id}`} className="h-11 rounded-none bg-black text-white hover:bg-black/80">
                        Ouvrir <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
